import i18n from "i18next";
import {initReactI18next} from "react-i18next";
import enJson from "../../locales/en.default.json";
import deJson from "../../locales/de.json";
import frJson from "../../locales/fr.json";
import itJson from "../../locales/it.json";
import esJson from "../../locales/es.json";
import {getLanguageFromRegion} from "./regions";

// Validate that JSON files were loaded correctly
if (!enJson || typeof enJson !== 'object') {
  console.error("Failed to load en.default.json - translations may not work correctly");
} else {
  console.log("[i18n] en.default.json loaded, has faq:", !!enJson.faq, "total keys:", Object.keys(enJson).length);
}
if (!deJson || typeof deJson !== 'object') {
  console.error("Failed to load de.json - translations may not work correctly");
} else {
  console.log("[i18n] de.json loaded, has faq:", !!deJson.faq, "total keys:", Object.keys(deJson).length);
  if (deJson.faq) {
    console.log("[i18n] de.json faq keys:", Object.keys(deJson.faq));
  }
}

// Merge window.translationsObject as fallback (loaded from Liquid script-translation.liquid)
// This ensures translations work even if JSON imports fail in production
// Only use liquidTranslations for keys that are missing from JSON (to avoid overriding correct JSON values)
const liquidTranslations = (typeof window !== 'undefined' && window.translationsObject) || {};

// Helper function to merge translations, preferring JSON over Liquid
const mergeTranslations = (jsonTranslations, liquidTranslations) => {
  const merged = { ...jsonTranslations };
  
  // Only add liquid translations for keys that don't exist in JSON
  Object.keys(liquidTranslations).forEach(key => {
    if (!(key in merged)) {
      merged[key] = liquidTranslations[key];
    } else if (typeof liquidTranslations[key] === 'object' && typeof merged[key] === 'object') {
      // Recursively merge nested objects, preferring JSON values
      merged[key] = mergeTranslations(merged[key], liquidTranslations[key]);
    }
    // If key exists in JSON, keep JSON value (don't override)
  });
  
  return merged;
};

const resources = {
  en: {
    translation: mergeTranslations(enJson || {}, liquidTranslations),
  },
  de: {
    translation: mergeTranslations(deJson || {}, liquidTranslations),
  },
  fr: {
    translation: mergeTranslations(frJson || {}, liquidTranslations),
  },
  it: {
    translation: mergeTranslations(itJson || {}, liquidTranslations),
  },
  es: {
    translation: mergeTranslations(esJson || {}, liquidTranslations),
  },
};

// Map region to language code (ch->de, at->de, uk->en, etc)
const region = window.Theme?.region;
const lng = region ? getLanguageFromRegion(region) : (window.Shopify?.locale || 'de');

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng,
    fallbackLng: "de",
    debug: process.env.NODE_ENV !== 'production',
    interpolation: {
      escapeValue: false,
    },
    // Add missing key handler to help debug - always log in production for FAQ keys
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (key.startsWith('faq.')) {
        console.warn(`[i18n] Missing FAQ translation key: ${key} for languages: ${lngs.join(', ')}`);
        // Log what's actually in the resources for debugging
        lngs.forEach(lng => {
          if (resources[lng]?.translation) {
            const hasFaq = !!resources[lng].translation.faq;
            console.log(`[i18n] Language ${lng} has faq object: ${hasFaq}`);
            if (hasFaq) {
              console.log(`[i18n] Language ${lng} faq keys:`, Object.keys(resources[lng].translation.faq));
            }
          }
        });
      }
    },
  });

// Listen for region changes
window.addEventListener('theme:region:change', (event) => {
  // Don't override manual language changes (e.g., from FAQ language selector)
  if (window.__i18nManualLanguageChange) {
    return;
  }
  
  const newRegion = event.detail?.region || window.Theme?.region;
  if (newRegion) {
    const newLanguage = getLanguageFromRegion(newRegion);
    if (i18n.language !== newLanguage) {
      i18n.changeLanguage(newLanguage);
    }
  }
});

// Also check for region changes via MutationObserver as a fallback
const checkRegionChange = () => {
  // Don't override manual language changes (e.g., from FAQ language selector)
  if (window.__i18nManualLanguageChange) {
    return;
  }
  
  const currentRegion = window.Theme?.region;
  if (currentRegion) {
    const currentLanguage = getLanguageFromRegion(currentRegion);
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }
};

// Check periodically for region changes (as fallback)
setInterval(checkRegionChange, 1000);

export default i18n;
